import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useNode, useEditor } from '@splashthat/splash-cms-engine';
import TextEditable from 'common/components/TouchpointEditor/TextEditable';
import { getCustomCssClassName } from 'common/components/TouchpointEditor/tools/CustomCssTools/CustomCssTools';

import { BRAND_LIBRARY_LIGHT_TEXT_COLOR, BRAND_LIBRARY_SUBHEADLINE_TEXT } from 'common/constants/brandLibrary';

import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import { ParagraphContent } from './Paragraph.styles';

const Paragraph = React.forwardRef(
    (
        { styles: { fontSize, fontColor, fontFamily }, className, meta: { content, visible }, selected, hovered },
        ref,
    ) => {
        const { theme } = useTouchpointData();
        const node = useNode();
        const customCssClassName = getCustomCssClassName(node);
        const joinedClassNames = [className, customCssClassName || ''].join(' ');
        const editor = useEditor((state) => ({ editing: state.editing }));
        const setContent = useCallback(
            (text) =>
                node.actions.setProp((editedNode) => {
                    editedNode.meta.content = text; // eslint-disable-line no-param-reassign
                    return editedNode;
                }),
            [],
        );

        return visible ? (
            <ParagraphContent
                className={joinedClassNames}
                selected={selected}
                hovered={hovered}
                onMouseLeave={node.actions.onMouseLeave()}
                onMouseMove={node.actions.onMouseMove()}
                onClick={node.actions.onClick()}
                $fontSize={fontSize}
                $fontColor={theme.colors[fontColor]}
                $fontFamily={theme.activeFonts[fontFamily]}
                ref={ref}
            >
                <TextEditable setContent={setContent} disabled={!editor.editing || (!selected && !hovered)}>
                    {content}
                </TextEditable>
            </ParagraphContent>
        ) : null;
    },
);

Paragraph.splash = {
    related: {
        settings: React.lazy(() => import('./Paragraph.tools')),
    },
    defaultProps: {
        styles: {
            fontSize: 20,
            fontColor: BRAND_LIBRARY_LIGHT_TEXT_COLOR,
            fontFamily: BRAND_LIBRARY_SUBHEADLINE_TEXT,
        },
        meta: {
            // eslint-disable-next-line max-len
            content:
                'We’re excited to host you at the event. View the agenda below, and check back for updates to speakers and sessions as the event gets closer.',
            visible: true,
        },
    },
};

Paragraph.propTypes = {
    className: PropTypes.string,
    meta: PropTypes.shape({
        content: PropTypes.string.isRequired,
        visible: PropTypes.bool.isRequired,
    }).isRequired,
    selected: PropTypes.bool.isRequired,
    hovered: PropTypes.bool.isRequired,
    styles: PropTypes.shape({
        fontFamily: PropTypes.number,
        fontSize: PropTypes.number,
        fontColor: PropTypes.number,
    }).isRequired,
};

Paragraph.defaultProps = {
    className: '',
};

export default Paragraph;
